import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { jourLong } from "@/lib/dates";

const TYPE_COLORS = {
  travaille: "#002FA7",
  repos: "#868E96",
  conge: "#0066FF",
  conge_paye: "#059669",
  absence_maladie: "#E60000",
  jour_ferie: "#FFD700",
};
const TYPE_LABELS = {
  travaille: "Travaillé", repos: "Repos", conge: "Congé",
  conge_paye: "Congé payé", absence_maladie: "Maladie", jour_ferie: "Férié",
};

function startOfWeek(d) { const date = new Date(d); const day = date.getDay(); const diff = (day === 0 ? -6 : 1) - day; date.setDate(date.getDate() + diff); date.setHours(0,0,0,0); return date; }
function fmt(d) { return d.toISOString().slice(0, 10); }
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }

export default function SalariePlanning() {
  const [viewMode, setViewMode] = useState("week");
  const [anchor, setAnchor] = useState(new Date());
  const [entries, setEntries] = useState([]);

  const { start, end, dates } = useMemo(() => {
    let start, end;
    if (viewMode === "week") { start = startOfWeek(anchor); end = addDays(start, 6); }
    else { start = new Date(anchor.getFullYear(), anchor.getMonth(), 1); end = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0); }
    const dates = [];
    for (let d = new Date(start); d <= end; d = addDays(d, 1)) dates.push(new Date(d));
    return { start, end, dates };
  }, [viewMode, anchor]);

  useEffect(() => {
    api.get("/salarie/planning", { params: { date_start: fmt(start), date_end: fmt(end) } })
      .then((r) => setEntries(r.data)).catch(() => {});
  }, [viewMode, anchor]);

  const entryAt = (d) => entries.find((e) => e.date === fmt(d));

  const shift = (dir) => {
    if (viewMode === "week") setAnchor(addDays(anchor, dir * 7));
    else { const c = new Date(anchor); c.setMonth(c.getMonth() + dir); setAnchor(c); }
  };
  const title = viewMode === "week" ? `${start.toLocaleDateString("fr-FR")} → ${end.toLocaleDateString("fr-FR")}` : anchor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  return (
    <div className="min-h-full">
      <PageHeader eyebrow="Planning" title="Mon planning" sub="Consultez vos jours travaillés, repos et absences."
        right={
          <div className="flex items-center gap-2">
            <Select value={viewMode} onValueChange={setViewMode}>
              <SelectTrigger data-testid="planning-view-toggle" className="w-32 h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="week">Semaine</SelectItem>
                <SelectItem value="month">Mois</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => shift(-1)} data-testid="planning-prev" className="h-10"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="h-10 px-4 border border-[#DEE2E6] bg-white flex items-center text-sm font-semibold min-w-[240px] justify-center">{title}</div>
            <Button variant="outline" onClick={() => shift(1)} data-testid="planning-next" className="h-10"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        }
      />
      <div className="p-6 lg:p-10">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="salarie-planning-grid">
          {dates.map((d) => {
            const e = entryAt(d);
            const bg = e ? TYPE_COLORS[e.type] : "#FFFFFF";
            const fg = e && e.type !== "jour_ferie" ? "white" : "#0A0A0A";
            return (
              <div key={d.toISOString()} className="border border-[#DEE2E6] p-4 min-h-[100px]" style={{ background: bg, color: fg }} data-testid={`day-${fmt(d)}`}>
                <div className="text-xs uppercase tracking-wider font-semibold opacity-75">{jourLong(d)}</div>
                <div className="font-display font-bold text-2xl tracking-tight">{d.getDate()}</div>
                <div className="text-xs opacity-75">{d.toLocaleDateString("fr-FR", { month: "short" })}</div>
                {e && (
                  <div className="mt-2 text-xs font-semibold">
                    {TYPE_LABELS[e.type]}
                    {e.horaire_debut && <div className="font-mono text-xs">{e.horaire_debut}→{e.horaire_fin}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
